import type {
  DynamicDocumentationEligibilityRequest,
  StatifiedDocumentationEligibilityRequest,
} from 'apiConnector/form/documentation';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { Underlying } from 'models/underlying';
import type { DataById } from 'state/formData/formData.model';
import { getFormEnrichedData } from 'state/formData/formData.selectors';
import { commonFormActions } from 'state/forms/actions/form';
import { getDynamicInstrumentContractName, getDynamicInstrumentName, getFormInfos } from 'state/selectors';
import type { Thunk } from 'state/state.common';

import type { HandledWorkspace } from './models';

function getStringValue(enrichedData: DataById | undefined, key: string): string | undefined {
  const value = enrichedData?.[key];
  return typeof value === 'string' ? value : undefined;
}

function getUnderlyingsValue(enrichedData: DataById | undefined): Underlying[] | undefined {
  const value = enrichedData?.Underlyings;
  return Array.isArray(value) ? (value as Underlying[]) : undefined;
}

function getSalesCounterpartValue(enrichedData: DataById | undefined): ISalesCounterpart | undefined {
  const value = enrichedData?.SelectedOnBehalfClient;
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as ISalesCounterpart;
  if (typeof candidate.id !== 'string' || typeof candidate.label !== 'string') {
    return undefined;
  }

  return candidate;
}

function buildCommonRequest(negotiationId: string, quoteId: string | undefined, enrichedData: DataById | undefined) {
  return {
    negotiationId,
    quoteId,
    wrapper: getStringValue(enrichedData, 'Wrapper'),
    underlyings: getUnderlyingsValue(enrichedData),
    selectedOnBehalfClient: getSalesCounterpartValue(enrichedData),
  };
}

function buildStatifiedRequest(
  negotiationId: string,
  quoteId: string | undefined,
  enrichedData: DataById | undefined,
): StatifiedDocumentationEligibilityRequest | undefined {
  const productSubtype = getStringValue(enrichedData, 'ProductSubtype');
  if (!productSubtype) {
    return undefined;
  }

  return {
    ...buildCommonRequest(negotiationId, quoteId, enrichedData),
    productSubtype,
    productType: getStringValue(enrichedData, 'ProductType'),
  };
}

function buildDynamicRequest(
  negotiationId: string,
  quoteId: string | undefined,
  enrichedData: DataById | undefined,
  instrumentName: string | undefined,
  instrumentContractName: string | undefined,
): DynamicDocumentationEligibilityRequest | undefined {
  if (!instrumentName || !instrumentContractName) {
    return undefined;
  }

  return {
    ...buildCommonRequest(negotiationId, quoteId, enrichedData),
    instrumentName,
    instrumentContractName,
  };
}

function buildDocumentationRequest(
  workspace: HandledWorkspace,
  negotiationId: string,
  quoteId: string | undefined,
  enrichedData: DataById | undefined,
  instrumentName?: string,
  instrumentContractName?: string,
) {
  if (workspace === 'dynamic') {
    return buildDynamicRequest(negotiationId, quoteId, enrichedData, instrumentName, instrumentContractName);
  }

  return buildStatifiedRequest(negotiationId, quoteId, enrichedData);
}

export function triggerDocumentationFetchThunk(tabId: string, workspace: HandledWorkspace): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const formInfos = getFormInfos(state, workspace, tabId);
    const negotiationId = formInfos?.negotiationId;
    if (!negotiationId) {
      return;
    }
    const quoteId = formInfos?.quoteId;

    const enrichedData = getFormEnrichedData(state, tabId);
    const request = buildDocumentationRequest(
      workspace,
      negotiationId,
      quoteId,
      enrichedData,
      workspace === 'dynamic' ? getDynamicInstrumentName(state, tabId) : undefined,
      workspace === 'dynamic' ? getDynamicInstrumentContractName(state, tabId) : undefined,
    );
    if (!request) {
      return;
    }

    dispatch(commonFormActions.formDocumentationFetchRequested(workspace, tabId, request));
  };
}
